/**
 * SOUND-643 — ordering rules for the registration Actions panel.
 *
 * The panel is ONE flat list. Ordering carries the meaning that grouping and
 * dividers used to, so the rules live here with tests rather than inside JSX,
 * which this repo cannot test.
 *
 * The list reads top-to-bottom as "what would you most likely do next":
 *
 *   1. status transitions — moving the application forward
 *   2. Edit draft         — while it is still a draft
 *   3. Create MID Application
 *   4. Download Application
 *   5. Reopen             — always last
 *
 * Two rules earn their place:
 *
 * REOPEN LAST. On an approved application the only legal transition is
 * `approved → needs_info`, which is an undo. Left in position 1 it would sit at
 * the top of the panel at exactly the moment the obvious next step is Create MID
 * Application. It is not a forward action and should never lead the list.
 *
 * CREATE MID ABOVE DOWNLOAD. Once a registration is approved the team is ready
 * to apply to banks, so with Reopen demoted, Create MID Application becomes the
 * first thing in the panel — which is the actual next step in the workflow.
 */

/**
 * Is this transition an undo of a decision rather than a step forward?
 *
 * SOUND-577: `rejected → needs_info` is one too. A rejected application used to
 * offer `rejected → submitted` rendered as "Mark Submitted" at the TOP of the
 * panel — reading like a routine next step when it is the same act as reopening
 * an approval: undoing a decision already communicated to the merchant. The
 * edge itself now routes through needs_info, matching the approved path.
 *
 * Both edges are the only legal move out of a decided state, so in both cases
 * this is the whole of `forward`, and demoting it is what stops an undo leading
 * the list.
 */
export function isReopenTransition(currentStatus, targetStatus) {
  return (
    (currentStatus === "approved" && targetStatus === "needs_info") ||
    (currentStatus === "rejected" && targetStatus === "needs_info")
  );
}

/**
 * Split legal transitions into the ones that move the application forward and
 * the reopen action, which renders at the bottom of the panel.
 *
 * @returns {{ forward: string[], reopen: string[] }}
 */
export function orderStatusTransitions(currentStatus, transitions = []) {
  const forward = [];
  const reopen = [];
  for (const target of transitions) {
    if (isReopenTransition(currentStatus, target)) reopen.push(target);
    else forward.push(target);
  }
  return { forward, reopen };
}
